import { isEqual, cloneDeep } from "lodash";
import { FORM_KEYS } from "../pages/self-service/constants";

/**
 * @desc Push to analytic data layer.
 */
export const pushEvent = (action, parameters) => {
  if (typeof window.gtag === typeof Function) {
    window.gtag("event", action, parameters);
  }
};

/**
 * @desc A HOF for determing if a convention is live.
 */
export const isConventionLive = (brand, liveConventions) => (cname) => {
  return !!liveConventions.find(
    (liveConvention) =>
      liveConvention.brand === brand && liveConvention.cname === cname
  );
};

/**
 * @desc Determines if a value is empty.
 */
export const isEmpty = (value) => [null, undefined, ""].includes(value);

/**
 * @desc Calculate shallow object difference.
 */
export const difference = (original, target) => {
  return Object.entries(target)
    .filter(([key, value]) => {
      if (Array.isArray(value)) {
        return !isEqual(
          cloneDeep(original[key]).sort(),
          cloneDeep(value).sort()
        );
      }
      if (isEmpty(original[key]) && isEmpty(value)) return false;
      return !isEqual(original[key], value);
    })
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
};

/**
 * @desc Convert convention form to Convention Object.
 */
export const mapFormToConventionObject = (data) => {
  const result = {
    abbrev: data[FORM_KEYS.ABBREV],
    announcement: data[FORM_KEYS.ANNOUNCEMENT],
    backgroundImage: data[FORM_KEYS.BACKGROUND_IMAGE],
    darkMode: data[FORM_KEYS.DARK_MODE],
    header: data[FORM_KEYS.TITLE],
    name: data[FORM_KEYS.CNAME],
  };
  const backgroundStyle = {};
  if (data[FORM_KEYS.BACKGROUND_COLOR]) {
    backgroundStyle.backgroundColor = data[FORM_KEYS.BACKGROUND_COLOR];
  }

  if (Object.entries(backgroundStyle).length) {
    result.backgroundStyle = backgroundStyle;
  }

  return result;
};

/**
 * @desc Convert product object to product form.
 */
export const mapProductObjectToForm = (product) => ({
  [FORM_KEYS.PRODUCT_IS_SOLD_OUT]: product.isSoldOut,
  [FORM_KEYS.PRODUCT_NAME]: product.name,
  [FORM_KEYS.PRODUCT_EYEBROW]: product.eyebrow,
  [FORM_KEYS.PRODUCT_LICENSE]: product.license,
  [FORM_KEYS.PRODUCT_FOOTER]: product.footer,
  [FORM_KEYS.PRODUCT_NUMBER]: product.number,
  [FORM_KEYS.PRODUCT_INDEX]: product.index,
  [FORM_KEYS.PRODUCT_PRICE]: product.price,
  [FORM_KEYS.PRODUCT_IMAGE]: product.image,
  [FORM_KEYS.PRODUCT_SKU]: product.sku,
});

/**
 * @desc Convert product form to product object.
 */
export const mapFormToProductObject = (formProduct) => {
  return {
    isSoldOut: formProduct[FORM_KEYS.PRODUCT_IS_SOLD_OUT],
    name: formProduct[FORM_KEYS.PRODUCT_NAME],
    eyebrow: formProduct[FORM_KEYS.PRODUCT_EYEBROW],
    license: formProduct[FORM_KEYS.PRODUCT_LICENSE],
    footer: formProduct[FORM_KEYS.PRODUCT_FOOTER],
    number: formProduct[FORM_KEYS.PRODUCT_NUMBER],
    index:
      formProduct[FORM_KEYS.PRODUCT_INDEX] &&
      Number(formProduct[FORM_KEYS.PRODUCT_INDEX]),
    price:
      formProduct[FORM_KEYS.PRODUCT_PRICE] &&
      Number(formProduct[FORM_KEYS.PRODUCT_PRICE]),
    image: formProduct[FORM_KEYS.PRODUCT_IMAGE],
    sku: formProduct[FORM_KEYS.PRODUCT_SKU],
  };
};

/**
 * @desc Calculate difference between products and form products.
 * Return an array of product objects with differences.
 */
export const productDifference = (products = [], formProducts) => {
  return Object.values(formProducts)
    .map((item) => mapFormToProductObject(item))
    .reduce((acc, newProduct) => {
      const original = products.find(
        (product) => product.sku === newProduct.sku
      );
      let result = newProduct;
      if (original) {
        const diff = difference(original, newProduct);
        if (Object.entries(diff).length > 0) {
          result = {
            sku: original.sku,
            ...diff,
          };
        } else {
          return acc;
        }
      }
      acc.push(result);
      return acc;
    }, []);
};

/**
 * @desc Calculate convention form difference.
 */
export const getFormDifference = ({ conventions, form }) => {
  if (!form[FORM_KEYS.CNAME]) return {};
  const target = mapFormToConventionObject(form);
  const original = conventions.find(
    (convention) => convention.name === form[FORM_KEYS.CNAME]
  );
  if (original) {
    const result = difference(original, target);
    const products = productDifference(
      original.products || [],
      form[FORM_KEYS.PRODUCTS]
    );
    if (products.length) result.products = products;
    return result;
  }
  return target;
};

/**
 * @desc Product sort.
 */
export const productSort = (a, b) => {
  const aValue = a.index || a.number || 0;
  const bValue = b.index || b.number || 0;
  return aValue - bValue;
};
